import _ from 'underscore';

import {assert} from 'Asserts';
import * as EventConstants from 'constants/EventConstants';
import {Batch} from 'constants/SampleConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {BioentitySummary} from 'folder-items/FileConstants';
import AppDispatcher from 'lib/Dispatcher';
import notifications from 'notifications';
import * as ModelActions from 'redux-stores/ModelActions';
import {BenchlingDispatch} from 'redux-stores/Store';
import {
  NormalizedRequestResponseSample,
  RequestResponse,
  RequestResponseSample,
} from 'requests/RequestConstants';
import * as RequestReduxActions from 'requests/RequestReduxActions';
import * as RequestTaskReduxActions from 'requests/tasks/RequestTaskReduxActions';
import * as SampleGroupReduxActions from 'workflows/samples/SampleGroupReduxActions';
import * as StageRunActions from 'workflows/StageRunActions';

import {
  FulfillmentStatus,
  RequestFulfillment,
  RequestFulfillmentDefinition,
  RequestFulfillmentUpdate,
} from './FulfillmentConstants';
import * as FulfillmentDAO from './FulfillmentDAO';
import {fulfillmentDefinitionsToType, fulfillmentTypeToPluralized} from './FulfillmentType';

export enum RequestFulfillmentActionType {
  REQUEST_FULFILLMENTS_FETCHED_FOR_REQUESTS = 'REQUEST_FULFILLMENTS_FETCHED_FOR_REQUESTS',
  REQUEST_RESPONSE_FETCH_COMPLETED = 'REQUEST_RESPONSE_FETCH_COMPLETED',
  REQUEST_FULFILLMENTS_FETCHED = 'REQUEST_FULFILLMENTS_FETCHED',
  REQUEST_FULFILLMENTS_FETCH_STARTED = 'REQUEST_FULFILLMENTS_FETCH_STARTED',
  REQUEST_FULFILLMENT_UPDATED = 'REQUEST_FULFILLMENT_UPDATED',
  REQUEST_FULFILLMENTS_DELETED = 'REQUEST_FULFILLMENTS_DELETED',
  FULFILLMENTS_LOADED_FOR_ENTRY = 'FULFILLMENTS_LOADED_FOR_ENTRY',
}

export interface FulfillmentsFetchedForRequestsAction {
  type: RequestFulfillmentActionType.REQUEST_FULFILLMENTS_FETCHED_FOR_REQUESTS;
  requestIds: Array<string>;
  fulfillments: Array<RequestFulfillment>;
}

export function fulfillmentsFetchedForRequests(
  requestIds: Array<string>,
  fulfillments: Array<RequestFulfillment>
): FulfillmentsFetchedForRequestsAction {
  return {
    type: RequestFulfillmentActionType.REQUEST_FULFILLMENTS_FETCHED_FOR_REQUESTS,
    requestIds,
    fulfillments,
  };
}

export interface RequestResponseFetchCompletedAction {
  type: RequestFulfillmentActionType.REQUEST_RESPONSE_FETCH_COMPLETED;
  requestId: string;
  samples: Array<NormalizedRequestResponseSample>;
}

export function requestResponseFetchCompleted(
  requestId: string,
  samples: Array<NormalizedRequestResponseSample>
): RequestResponseFetchCompletedAction {
  return {
    type: RequestFulfillmentActionType.REQUEST_RESPONSE_FETCH_COMPLETED,
    requestId,
    samples,
  };
}

function dispatchSamplesFetched(dispatch: BenchlingDispatch, samples: Array<RequestResponseSample>): void {
  // TODO: This should be cleaned up to use StageRunSamples, otherwise we won't have container information.
  const fileSummariesById: {[bioentityId: string]: BioentitySummary} = {};
  const batchesById: {[batchId: string]: Batch} = {};

  for (const {bioentity, batch} of samples) {
    if (bioentity) {
      fileSummariesById[bioentity.id] = bioentity;
    } else if (batch) {
      batchesById[batch.id] = batch;
    } else {
      assert(false, 'Request response sample should have either a batch or a file');
    }
  }

  if (!_.isEmpty(fileSummariesById)) {
    dispatch(ModelActions.filesFetchCompleted(fileSummariesById));
  }
  if (!_.isEmpty(batchesById)) {
    AppDispatcher.dispatch({
      actionType: EventConstants.BATCHES_BULK_FETCH_COMPLETED,
      batchesById,
    });
  }
}

export function fetchRequestResponse(requestId: string): (dispatch: BenchlingDispatch) => void {
  // A request response is essentially the sumamry of all of the fulfillments - they are used to find
  // registered samples and collected results.
  return (dispatch) =>
    FulfillmentDAO.fetchRequestResponse(requestId).then(
      ({samples, fulfillments, entrySummariesById, workflowSummaries}: RequestResponse) => {
        dispatchSamplesFetched(dispatch, samples);

        dispatch(ModelActions.entriesFetchCompleted(entrySummariesById));
        dispatch({
          type: ModelActions.ModelsActionType.WORKFLOW_WORKFLOW_SUMMARIES_FETCH_COMPLETED,
          workflowSummaries,
        });
        dispatch(fulfillmentsFetchedForRequests([requestId], fulfillments));

        const normalizedSamples = samples.map(({bioentity, batch, status}) => ({
          bioentityId: bioentity ? bioentity.id : null,
          batchId: batch ? batch.id : null,
          status,
        }));
        dispatch(requestResponseFetchCompleted(requestId, normalizedSamples));

        return {samples};
      }
    );
}

export interface RequestFulfillmentsFetchedAction {
  type: RequestFulfillmentActionType.REQUEST_FULFILLMENTS_FETCHED;
  fulfillments: Array<RequestFulfillment>;
}

export function requestFulfillmentsFetched(
  fulfillments: Array<RequestFulfillment>
): RequestFulfillmentsFetchedAction {
  return {
    type: RequestFulfillmentActionType.REQUEST_FULFILLMENTS_FETCHED,
    fulfillments,
  };
}

export function fetchRequestFulfillments(
  fulfillmentIds: Array<string>
): (dispatch: BenchlingDispatch) => Promise<void> {
  return async (dispatch) => {
    const {
      requestFulfillments,
      sampleGroups,
      stageRunSamples,
      batchesById,
      containersById,
      fileSummariesById,
      requests,
      requestTasks,
    } = await FulfillmentDAO.fetchRequestFulfillments(fulfillmentIds);

    dispatch(requestFulfillmentsFetched(requestFulfillments));
    StageRunActions.dispatchStageRunSamplesFetched({
      stageRunSamples,
      batchesById,
      containersById,
      fileSummariesById,
    });
    dispatch(SampleGroupReduxActions.sampleGroupsFetchCompleted(sampleGroups));
    dispatch(RequestReduxActions.requestsFetchCompleted(requests));
    dispatch(RequestTaskReduxActions.requestTasksFetched(requestTasks));
  };
}

export interface RequestFulfillmentsFetchStartedAction {
  type: RequestFulfillmentActionType.REQUEST_FULFILLMENTS_FETCH_STARTED;
  requestIds: Array<string>;
}

function requestFulfillmentsFetchStarted(requestIds: Array<string>): RequestFulfillmentsFetchStartedAction {
  return {
    type: RequestFulfillmentActionType.REQUEST_FULFILLMENTS_FETCH_STARTED,
    requestIds,
  };
}

export function listFulfillmentsForRequests(
  requestIds: Array<string>
): (dispatch: BenchlingDispatch) => Promise<void> {
  return async (dispatch) => {
    dispatch(requestFulfillmentsFetchStarted(requestIds));

    const {requestFulfillments} = await FulfillmentDAO.listFulfillmentsForRequests(requestIds);
    dispatch(fulfillmentsFetchedForRequests(requestIds, requestFulfillments));
  };
}

export interface RequestFulfillmentUpdatedAction {
  type: RequestFulfillmentActionType.REQUEST_FULFILLMENT_UPDATED;
  fulfillment: RequestFulfillment;
}

function requestFulfillmentUpdated(fulfillment: RequestFulfillment): RequestFulfillmentUpdatedAction {
  return {
    type: RequestFulfillmentActionType.REQUEST_FULFILLMENT_UPDATED,
    fulfillment,
  };
}

export function updateFulfillment(
  fulfillmentId: string,
  newFulfillment: {statusAfterReview: FulfillmentStatus}
): (dispatch: BenchlingDispatch) => void {
  return (dispatch) => {
    FulfillmentDAO.updateFulfillment(fulfillmentId, newFulfillment).then(({requestFulfillment}) => {
      dispatch(requestFulfillmentUpdated(requestFulfillment));
    }, notifications.notifyError);
  };
}

export function bulkUpdateFulfillments(
  fulfillmentUpdates: Array<RequestFulfillmentUpdate>
): (dispatch: BenchlingDispatch) => Promise<void> {
  return async (dispatch) => {
    const {requestFulfillments: newFulfillments} =
      await FulfillmentDAO.bulkUpdateFulfillments(fulfillmentUpdates);
    dispatch(requestFulfillmentsFetched(newFulfillments));
    notifications.success('Requests updated successfully.');
  };
}

export interface RequestFulfillmentsDeletedAction {
  type: RequestFulfillmentActionType.REQUEST_FULFILLMENTS_DELETED;
  fulfillmentIds: Array<string>;
}

export function requestFulfillmentsDeleted(fulfillmentIds: Array<string>): RequestFulfillmentsDeletedAction {
  return {
    type: RequestFulfillmentActionType.REQUEST_FULFILLMENTS_DELETED,
    fulfillmentIds,
  };
}

export function bulkRemoveFulfillments(
  fulfillmentIds: Array<string>
): (dispatch: BenchlingDispatch) => Promise<void> {
  return async (dispatch) => {
    await FulfillmentDAO.bulkRemoveFulfillments(fulfillmentIds);
    dispatch(requestFulfillmentsDeleted(fulfillmentIds));
  };
}

export interface FulfillmentsLoadedForEntryAction {
  type: RequestFulfillmentActionType.FULFILLMENTS_LOADED_FOR_ENTRY;
  entryId: string;
  requestFulfillments: Array<RequestFulfillment>;
}

export function fulfillmentsLoadedForEntry({
  entryId,
  requestFulfillments,
}: {
  entryId: string;
  requestFulfillments: Array<RequestFulfillment>;
}): FulfillmentsLoadedForEntryAction {
  return {
    type: RequestFulfillmentActionType.FULFILLMENTS_LOADED_FOR_ENTRY,
    entryId,
    requestFulfillments,
  };
}

export function executeIntoExistingEntry(
  entryId: string,
  fulfillmentDefs: Array<RequestFulfillmentDefinition>
): (dispatch: BenchlingDispatch) => Promise<void> {
  return async (dispatch) => {
    const {entryFulfillments, numDuplicates} = await FulfillmentDAO.executeIntoExistingEntry(
      entryId,
      fulfillmentDefs
    );
    if (numDuplicates) {
      const pluralizedType = fulfillmentTypeToPluralized(fulfillmentDefinitionsToType(fulfillmentDefs));
      notifications.warn(
        `${numDuplicates} ${pluralizedType} were ignored since they were already present in this entry.`
      );
    }
    dispatch(fulfillmentsLoadedForEntry({entryId, requestFulfillments: entryFulfillments}));
    FileBrowserActions.openFile(entryId);
  };
}

export type RequestFulfillmentAction =
  | FulfillmentsFetchedForRequestsAction
  | RequestResponseFetchCompletedAction
  | RequestFulfillmentsFetchedAction
  | RequestFulfillmentsFetchStartedAction
  | RequestFulfillmentUpdatedAction
  | RequestFulfillmentsDeletedAction
  | FulfillmentsLoadedForEntryAction;
